<template>
  <div>
    <h3 class="tw:font-bold tw:mb-1">{{ $t('business_studio_plus_checkout_payment_details_header') }}</h3>
    <BaseCard noPadding class="tw:mb-6" dataTestId="payment-method-card">
      <div v-if="isLoading" class="tw:p-4">
        <div class="tw:animate-pulse tw:w-full tw:rounded-lg tw:bg-gray-300 tw:h-[150px]" />
      </div>

      <PaymentMethodForm
        v-else
        ref="paymentMethodForm"
        :currency="currency"
        :accommodationId="currentAccommodationId"
        :productName="productName"
        :fetchingPaymentMethods="isFetching"
        :existingPaymentMethods="existingPaymentMethods"
        :availablePaymentMethods="availablePaymentMethodsData"
        :paypalConfig="paypalConfig"
        :selectedPaymentMethod="selectedPaymentMethod"
        @onShowNewFormCancel="onNewPaymentMethodFormCancel"
        @onSelectedValueChange="onSelectedPaymentMethodChange"
        @onZuoraCallbackSuccess="onZuoraCallbackSuccess"
        @onZuoraCallbackError="onZuoraCallbackError"
        @onZuoraClientValidationError="onZuoraClientValidationError"
      />
    </BaseCard>
    <h3 class="tw:font-bold tw:mb-1" ref="billingFormTitle">{{ $t('checkoutPage.alreadyAuthorized.billingInfo') }}</h3>
    <BaseCard noPadding class="tw:flex tw:flex-col tw:gap-3 tw:px-4 tw:py-6" dataTestId="billing-information-form">
      <BillingInformationForm
        ref="billingForm"
        :initialBillingFormValues="existingBillingInformation"
        @onValidateAllSuccess="onValidateBillingSuccess"
        @onValidateAllError="onValidateBillingError"
        @setBillingFormValid="setBillingFormValid"
      />
    </BaseCard>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useStore } from 'vuex'
import * as Sentry from '@sentry/vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import BillingInformationForm from '@/components/payment/BillingInformationForm.vue'
import PaymentMethodForm from '@/components/payment/PaymentMethodForm.vue'
import zuoraMetricConstants from '@/constants/zuoraMetricConstants'

import {
  useAvailablePaymentMethods,
  useErrorMetrics,
  useExistingPaymentMethods,
  useZuoraAccount,
  useZuoraNewAccountMutation,
  useZuoraUpdateAccountMutation,
} from '@/components/payment/queries'
import { countries } from '@/constants/countries.ts'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import { useSelectedPaymentMethodType, NEW_PAYMENT_METHOD_TYPES } from '@/composables/useSelectedPaymentMethodType'

const emit = defineEmits(['onFormValidationError', 'onFormValidationSuccess', 'onZuoraHostedPageLoadError'])

const store = useStore()

const capturePaymentError = (message, extra = {}) => {
  Sentry.withScope(scope => {
    scope.setTag('feature', 'payment-form')
    scope.setContext('paymentDetails', {
      accommodationId: selectedAccommodationId.value,
      productName: props.productName,
      selectedPaymentMethod: selectedPaymentMethod.value,
      ...extra,
    })
    Sentry.captureMessage(message, 'error')
  })
}

const props = defineProps({
  currency: {
    type: String,
    default: 'EUR',
  },
  accommodationId: Number,
  productName: String,
})
const paymentMethodForm = ref(null)
const currencyCode = computed(() => props.currency)
const currentAccommodationId = computed(() => props.accommodationId)
const { existingPaymentMethods } = useExistingPaymentMethods(currentAccommodationId)
const { isFetching, availablePaymentMethodsData, paypalConfig, refetch, isLoading } = useAvailablePaymentMethods(
  currentAccommodationId,
  currencyCode
)
const selectedOrganisationId = computed(() => store.state.session.selectedOrganisation?.id)
const token = computed(() => store.state.session?.studioToken)
const { zuoraAccountData } = useZuoraAccount(token)
const { createNewZuoraAccount } = useZuoraNewAccountMutation()
const { updateZuoraAccount } = useZuoraUpdateAccountMutation()
const { sendErrorMetrics } = useErrorMetrics()
const { language, userRole } = useSessionValues()

const billingForm = ref(null)
const selectedPaymentMethod = ref(null)
// Real vaulted PayPal payment-method id, captured from the PayPal button's
// onComplete callback. Used as defaultPaymentMethodId on submit — never the
// 'PAYPAL' type token.
const paypalPaymentMethodId = ref(null)
const newBillingInformation = ref(null)
const isSubmittingForm = ref(false)
const isZuoraHppLoadError = ref(false)
const isBillingFormValid = ref(true)
const countriesWithState = ['US', 'MX', 'CA', 'BR']
const ZUORA_SUBMIT_TIMEOUT_MS = 60000
let zuoraSubmitTimeoutId = null

const defaultPaymentMethodId = computed(
  () => existingPaymentMethods.value?.find(paymentMethod => paymentMethod.isDefault)?.id
)

const existingBillingInformation = computed(() => {
  if (
    !zuoraAccountData.value ||
    zuoraAccountData.value?.basicInfo === null ||
    zuoraAccountData.value?.billToContact === null
  )
    return null
  const countryCode = countries.find(country => country.name === zuoraAccountData.value?.billToContact?.country)?.code
  return {
    companyName: zuoraAccountData.value.basicInfo.name,
    email: zuoraAccountData.value.billToContact.workEmail,
    firstName: zuoraAccountData.value.billToContact.firstName,
    lastName: zuoraAccountData.value.billToContact.lastName,
    address: zuoraAccountData.value.billToContact.address1,
    city: zuoraAccountData.value.billToContact.city,
    zipCode: zuoraAccountData.value.billToContact.zipCode,
    country: countryCode,
    state: zuoraAccountData.value.billToContact.state,
    taxId: zuoraAccountData.value.taxInfo?.exemptCertificateId,
    noTaxId: zuoraAccountData.value.taxInfo?.exemptCertificateId ? false : true,
  }
})

const isSelectedPaymentMethodNew = computed(
  () => selectedPaymentMethod.value === 'CREDIT_CARD' || selectedPaymentMethod.value === 'SEPA'
)

const selectedPaymentMethodType = useSelectedPaymentMethodType(selectedPaymentMethod, existingPaymentMethods)

// True only when the selection is a real, existing payment-method id — never a
// new-method type token (CREDIT_CARD | SEPA | PAYPAL). Those tokens must not be
// sent as `defaultPaymentMethodId`; the real id arrives via onZuoraCallbackSuccess.
const isSelectedPaymentMethodExistingId = computed(
  () => !!selectedPaymentMethod.value && !NEW_PAYMENT_METHOD_TYPES.includes(selectedPaymentMethod.value)
)

const forceRender = async () => {
  await nextTick()
  refetch()
  isZuoraHppLoadError.value = false
}

const onSelectedPaymentMethodChange = value => {
  selectedPaymentMethod.value = value
  // Switching methods invalidates any previously vaulted PayPal id.
  paypalPaymentMethodId.value = null
}

const onNewPaymentMethodFormCancel = () => {
  if (isSelectedPaymentMethodNew.value) {
    selectedPaymentMethod.value = defaultPaymentMethodId.value
  }
}

const closeNewPaymentMethodForm = () => {
  paymentMethodForm.value?.onNewPaymentMethodFormCancel()
}

const clearZuoraSubmitTimeout = () => {
  if (zuoraSubmitTimeoutId) {
    clearTimeout(zuoraSubmitTimeoutId)
    zuoraSubmitTimeoutId = null
  }
}

const validateForm = () => {
  if (isSelectedPaymentMethodNew.value && isZuoraHppLoadError.value) {
    emit('onZuoraHostedPageLoadError')
    return
  }
  isSubmittingForm.value = true
  billingForm.value.validateAll()
}

const onValidateBillingSuccess = billingInfo => {
  newBillingInformation.value = billingInfo
  if (!selectedPaymentMethod.value) {
    isSubmittingForm.value = false
    emit('onFormValidationError')
    return
  }
  if (isSelectedPaymentMethodNew.value) {
    if (userRole.value === 'support') {
      isSubmittingForm.value = false
      return
    }
    try {
      clearZuoraSubmitTimeout()
      zuoraSubmitTimeoutId = setTimeout(() => {
        if (isSubmittingForm.value) {
          isSubmittingForm.value = false
          isZuoraHppLoadError.value = true
          if (props.accommodationId) {
            sendErrorMetrics({
              itemId: props.accommodationId,
              productName: props.productName,
              code: zuoraMetricConstants.metricErrorCodes.configurationError,
              message: 'Zuora HPP submit timeout - no callback received within 60s',
            })
          }
          capturePaymentError('Zuora HPP submit timeout - no callback received within 60s')
          emit('onFormValidationError')
        }
      }, ZUORA_SUBMIT_TIMEOUT_MS)
      Z.submit()
    } catch (error) {
      clearZuoraSubmitTimeout()
      isSubmittingForm.value = false
      isZuoraHppLoadError.value = true
      if (props.accommodationId) {
        sendErrorMetrics({
          itemId: props.accommodationId,
          productName: props.productName,
          code: zuoraMetricConstants.metricErrorCodes.configurationError,
          message: `Zuora HPP submit failed: ${error?.message || 'Unknown error'}`,
        })
      }
      capturePaymentError(`Zuora HPP submit failed: ${error?.message || 'Unknown error'}`, { error: String(error) })
      emit('onFormValidationError')
    }
    return
  }
  if (selectedPaymentMethod.value === 'PAYPAL') {
    // PayPal is authorized via its own button (onZuoraCallbackSuccess stores the
    // real id). Without it there's no payment method to set as default.
    if (!paypalPaymentMethodId.value) {
      isSubmittingForm.value = false
      capturePaymentError('PayPal submit attempted before authorization completed')
      emit('onFormValidationError')
      return
    }
    sendZuoraAccount(paypalPaymentMethodId.value)
    return
  }
  if (isSelectedPaymentMethodExistingId.value && selectedPaymentMethod.value !== defaultPaymentMethodId.value) {
    sendZuoraAccount(selectedPaymentMethod.value)
    return
  }
  sendZuoraAccount()
}

const onValidateBillingError = () => {
  isSubmittingForm.value = false
  emit('onFormValidationError')
}

const setBillingFormValid = isValid => {
  isBillingFormValid.value = isValid
}

const onZuoraClientValidationError = async (_, isAttemptExceedLimitationError) => {
  clearZuoraSubmitTimeout()
  isSubmittingForm.value = false
  if (isAttemptExceedLimitationError) {
    await forceRender()
    isZuoraHppLoadError.value = true
  }
  emit('onFormValidationError')
}

const onZuoraCallbackSuccess = async refId => {
  clearZuoraSubmitTimeout()
  if (selectedPaymentMethod.value === 'PAYPAL') {
    // PayPal completes via its own button, independently of the billing-form
    // submit. Remember the real vaulted payment-method id; the form submission
    // sends it as defaultPaymentMethodId.
    paypalPaymentMethodId.value = refId
    return
  }
  sendZuoraAccount(refId)
}

const onZuoraCallbackError = async (withRerender = false) => {
  clearZuoraSubmitTimeout()
  if (withRerender) {
    await forceRender()
  }
  isZuoraHppLoadError.value = true
  if (isSubmittingForm.value) {
    emit('onFormValidationError')
  }
  isSubmittingForm.value = false
}

const getTaxId = () => {
  if (newBillingInformation.value) {
    if (newBillingInformation.value?.noTaxId) return ''
    else if (newBillingInformation.value?.taxId) return newBillingInformation.value?.taxId
  } else return existingBillingInformation.value?.taxId
}

const sendZuoraAccount = async refId => {
  if (!existingBillingInformation.value) {
    const body = {
      name: newBillingInformation.value?.companyName,
      organization_id__c: selectedOrganisationId.value,
      thm_account_type__c: 'business studio',
      billCycleDay: 0,
      currency: currencyCode.value,
      thm_item_id__c: currentAccommodationId.value,
      autoPay: true,
      hpmCreditCardPaymentMethodId: refId,
      billToContact: {
        address1: newBillingInformation.value?.address,
        city: newBillingInformation.value?.city,
        country: newBillingInformation.value?.country,
        firstName: newBillingInformation.value?.firstName,
        lastName: newBillingInformation.value?.lastName,
        state: newBillingInformation.value?.state ?? '',
        workEmail: newBillingInformation.value?.email,
        zipCode: newBillingInformation.value?.zipCode,
      },
      taxInfo: {
        exemptCertificateId: newBillingInformation.value?.noTaxId ? '' : newBillingInformation.value?.taxId,
      },
      paymentMethodType: selectedPaymentMethodType.value,
    }

    createNewZuoraAccount(
      { body, language: language.value },
      {
        onSuccess: data => {
          if (data?.data?.success) {
            emit('onFormValidationSuccess')
            isSubmittingForm.value = false
          } else {
            forceRender()
            emit('onFormValidationError')
            isSubmittingForm.value = false
            sendErrorMetrics({
              itemId: props.accommodationId,
              productName: zuoraMetricConstants.productName.plus,
              code: zuoraMetricConstants.metricErrorCodes.billingInformation,
              message: data?.data,
            })
            capturePaymentError('Zuora account creation returned unsuccessful response', {
              responseData: JSON.stringify(data?.data),
            })
          }
        },
        onError: response => {
          emit('onFormValidationError')
          isSubmittingForm.value = false
          sendErrorMetrics({
            itemId: props.accommodationId,
            productName: zuoraMetricConstants.productName.plus,
            code: zuoraMetricConstants.metricErrorCodes.billingInformation,
            message: response?.response.data?.message,
          })
          capturePaymentError('Zuora account creation API error', {
            errorMessage: response?.response?.data?.message,
            status: response?.response?.status,
          })
        },
      }
    )
  } else {
    if (!newBillingInformation.value) {
      isSubmittingForm.value = false
      sendErrorMetrics({
        itemId: props.accommodationId,
        productName: props.productName,
        code: zuoraMetricConstants.metricErrorCodes.billingInformation,
        message: 'sendZuoraAccount called with existing billing info but no new billing data',
      })
      capturePaymentError('sendZuoraAccount called with existing billing info but no new billing data')
      emit('onFormValidationError')
      return
    }

    const nextZipCode = newBillingInformation.value.zipCode ?? existingBillingInformation.value.zipCode
    const nextCountry = newBillingInformation.value.country ?? existingBillingInformation.value.country
    const nextState = countriesWithState.includes(nextCountry) ? newBillingInformation.value.state : ''
    const nextTaxId = getTaxId()

    const hasDefaultPaymentMethodChange = Boolean(refId)
    const hasNameChange =
      newBillingInformation.value.companyName &&
      newBillingInformation.value.companyName !== existingBillingInformation.value.companyName
    const hasAddressChange =
      newBillingInformation.value.address &&
      newBillingInformation.value.address !== existingBillingInformation.value.address
    const hasCityChange =
      newBillingInformation.value.city && newBillingInformation.value.city !== existingBillingInformation.value.city
    const hasFirstNameChange =
      newBillingInformation.value.firstName &&
      newBillingInformation.value.firstName !== existingBillingInformation.value.firstName
    const hasLastNameChange =
      newBillingInformation.value.lastName &&
      newBillingInformation.value.lastName !== existingBillingInformation.value.lastName
    const hasEmailChange =
      newBillingInformation.value.email && newBillingInformation.value.email !== existingBillingInformation.value.email
    const hasZipCodeChange = nextZipCode !== existingBillingInformation.value.zipCode
    const hasCountryChange = nextCountry !== existingBillingInformation.value.country
    const hasStateChange = nextState !== existingBillingInformation.value.state
    const hasTaxIdChange = nextTaxId !== existingBillingInformation.value.taxId

    const hasAnyChange =
      hasDefaultPaymentMethodChange ||
      hasNameChange ||
      hasAddressChange ||
      hasCityChange ||
      hasFirstNameChange ||
      hasLastNameChange ||
      hasEmailChange ||
      hasZipCodeChange ||
      hasCountryChange ||
      hasStateChange ||
      hasTaxIdChange

    if (!hasAnyChange) {
      emit('onFormValidationSuccess')
      isSubmittingForm.value = false
      return
    }

    const body = {
      billToContact: {},
      taxInfo: {},
    }
    if (hasDefaultPaymentMethodChange) body.defaultPaymentMethodId = refId
    if (hasNameChange) body.name = newBillingInformation.value.companyName
    if (hasAddressChange) body.billToContact.address1 = newBillingInformation.value.address
    if (hasCityChange) body.billToContact.city = newBillingInformation.value.city
    if (hasFirstNameChange) body.billToContact.firstName = newBillingInformation.value.firstName
    if (hasLastNameChange) body.billToContact.lastName = newBillingInformation.value.lastName
    if (hasEmailChange) body.billToContact.workEmail = newBillingInformation.value.email

    // Zip code, country and taxId are always required when any update is sent.
    body.billToContact.zipCode = nextZipCode
    body.billToContact.country = nextCountry
    body.billToContact.state = nextState
    body.taxInfo.exemptCertificateId = nextTaxId
    body.paymentMethodType = selectedPaymentMethodType.value

    updateZuoraAccount(
      { body, language: language.value },
      {
        onSuccess: data => {
          if (data?.data?.success) {
            emit('onFormValidationSuccess')
            isSubmittingForm.value = false
          } else {
            forceRender()
            emit('onFormValidationError')
            isSubmittingForm.value = false
            sendErrorMetrics({
              itemId: props.accommodationId,
              productName: zuoraMetricConstants.productName.plus,
              code: zuoraMetricConstants.metricErrorCodes.billingInformation,
              message: data?.data,
            })
            capturePaymentError('Zuora account update returned unsuccessful response', {
              responseData: JSON.stringify(data?.data),
            })
          }
        },
        onError: response => {
          emit('onFormValidationError')
          isSubmittingForm.value = false
          sendErrorMetrics({
            itemId: props.accommodationId,
            productName: zuoraMetricConstants.productName.plus,
            code: zuoraMetricConstants.metricErrorCodes.billingInformation,
            message: response.response.data?.message,
          })
          capturePaymentError('Zuora account update API error', {
            errorMessage: response.response?.data?.message,
            status: response.response?.status,
          })
        },
      }
    )
  }
}

onMounted(() => {
  if (defaultPaymentMethodId.value) selectedPaymentMethod.value = defaultPaymentMethodId.value
})

onBeforeUnmount(() => {
  clearZuoraSubmitTimeout()
})

defineExpose({
  validateForm,
  closeNewPaymentMethodForm,
  selectedPaymentMethod,
  existingBillingInformation,
  isBillingFormValid,
  billingForm,
})
</script>
